import React, { useState, useRef } from 'react';
import { Box, Typography, Popper, Fade, Paper, useMediaQuery, useTheme } from '@mui/material';
import { Deck as DeckIcon } from '@mui/icons-material';

const getVisualCards = (deck, mode) => {
    const cards = Array.isArray(deck?.deckVisual?.cards) ? deck.deckVisual.cards : [];
    if (mode === 'thumbnail') return cards.slice(0, 4);
    if (mode === 'card') return cards;
    return cards;
};

const getGridColumns = (mode, count) => {
    if (mode === 'thumbnail') return 2;
    if (mode === 'card') return count > 6 ? 4 : 3;
    return count > 8 ? 4 : 3;
};

function QuantityBadge({ quantity, large = false }) {
    if (!quantity || quantity <= 1) return null;
    return (
        <Box
            sx={{
                position: 'absolute',
                top: large ? 8 : 4,
                right: large ? 8 : 4,
                width: large ? 36 : 22,
                height: large ? 36 : 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(10, 10, 20, 0.92)',
                border: `${large ? 2 : 1}px solid rgba(124, 58, 237, 0.7)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2
            }}
        >
            <Typography
                sx={{
                    color: 'white',
                    fontWeight: 800,
                    lineHeight: 1,
                    fontSize: large ? '1rem' : '0.6rem',
                    letterSpacing: '-0.02em'
                }}
            >
                {quantity}
            </Typography>
        </Box>
    );
}

function ArtNameCard({ card }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [tapOpen, setTapOpen] = useState(false);
    const timerRef = useRef(null);

    const imageUrl = card?.image?.thumbnail || card?.image?.detail;
    const detailUrl = card?.image?.detail || card?.image?.thumbnail;

    const handleMouseEnter = (e) => {
        if (isMobile) return;
        clearTimeout(timerRef.current);
        setAnchorEl(e.currentTarget);
    };

    const handleMouseLeave = () => {
        if (isMobile) return;
        timerRef.current = setTimeout(() => setAnchorEl(null), 80);
    };

    const handleTap = () => {
        if (!isMobile) return;
        setTapOpen(v => !v);
    };

    const popperOpen = isMobile ? tapOpen : Boolean(anchorEl);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleTap}
                sx={{
                    cursor: isMobile ? 'pointer' : 'default',
                    userSelect: 'none'
                }}
            >
                {/* Art crop */}
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: '8px 8px 0 0',
                        overflow: 'hidden',
                        aspectRatio: '4 / 3',
                        backgroundColor: 'rgba(76, 29, 149, 0.15)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderBottom: 'none',
                        transition: 'border-color 0.15s ease',
                        '&:hover': {
                            borderColor: 'rgba(124, 58, 237, 0.6)'
                        }
                    }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={card.name}
                            loading="lazy"
                            decoding="async"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'top',
                                display: 'block'
                            }}
                        />
                    ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DeckIcon sx={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: 22 }} />
                        </Box>
                    )}
                    <QuantityBadge quantity={card.quantity} large />
                </Box>

                {/* Name bar */}
                <Box
                    sx={{
                        borderRadius: '0 0 8px 8px',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderTop: 'none',
                        backgroundColor: 'rgba(17, 24, 39, 0.9)',
                        px: 0.75,
                        py: 0.5
                    }}
                >
                    <Typography
                        sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                        }}
                    >
                        {card.name}
                    </Typography>
                    {card.subtitle && (
                        <Typography
                            sx={{
                                color: 'rgba(196, 181, 253, 0.7)',
                                fontSize: '0.62rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                            }}
                        >
                            {card.subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Hover / tap popup: full card image */}
            <Popper
                open={popperOpen}
                anchorEl={isMobile ? null : anchorEl}
                placement="top"
                modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                transition
                style={{ zIndex: 1400, pointerEvents: 'none' }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={120}>
                        <Paper
                            elevation={12}
                            sx={{
                                borderRadius: '10px',
                                overflow: 'hidden',
                                border: '1px solid rgba(124, 58, 237, 0.4)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
                                width: 220,
                                pointerEvents: 'none'
                            }}
                        >
                            {detailUrl && (
                                <img
                                    src={detailUrl}
                                    alt={card.name}
                                    style={{ width: '100%', display: 'block' }}
                                />
                            )}
                        </Paper>
                    </Fade>
                )}
            </Popper>

            {/* Mobile: tap overlay */}
            {isMobile && tapOpen && detailUrl && (
                <Box
                    onClick={() => setTapOpen(false)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1400,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                    }}
                >
                    <img
                        src={detailUrl}
                        alt={card.name}
                        style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.8)' }}
                    />
                </Box>
            )}
        </Box>
    );
}

function FullCard({ card }) {
    const imageUrl = card?.image?.detail || card?.image?.thumbnail;

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(76, 29, 149, 0.12)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    aspectRatio: '5 / 7',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.55)'
                    }
                }}
            >
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={card.name}
                        loading="lazy"
                        decoding="async"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DeckIcon sx={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: 22 }} />
                    </Box>
                )}
                <QuantityBadge quantity={card.quantity} large />
            </Box>
        </Box>
    );
}

function CostGroupHeader({ cost, cards }) {
    const total = cards.reduce((s, c) => s + (c.quantity || 1), 0);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(124, 58, 237, 0.2)',
                    border: '1.5px solid rgba(124, 58, 237, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}
            >
                <Typography sx={{ color: '#c4b5fd', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>
                    {cost ?? '?'}
                </Typography>
            </Box>
            <Box sx={{ flex: 1, height: '1px', background: 'rgba(76, 29, 149, 0.3)' }} />
            <Typography sx={{ color: 'rgba(196, 181, 253, 0.45)', fontSize: '0.68rem' }}>
                {total} {total === 1 ? 'karta' : 'karty'}
            </Typography>
        </Box>
    );
}

const TYPE_ORDER = { character: 0, item: 1, location: 2, action: 3, song: 3 };

function sortCardsByType(cards) {
    return [...cards].sort((a, b) => {
        const ta = TYPE_ORDER[a.type?.toLowerCase()] ?? 0;
        const tb = TYPE_ORDER[b.type?.toLowerCase()] ?? 0;
        if (ta !== tb) return ta - tb;
        const ca = a.cost ?? 99, cb = b.cost ?? 99;
        if (ca !== cb) return ca - cb;
        return (a.sortNumber || 0) - (b.sortNumber || 0);
    });
}

function DeckVisualFull({ cards, viewMode = 'full-card', grouped = true }) {
    const sortedByCost = [...cards].sort((a, b) => {
        const ca = a.cost ?? 99;
        const cb = b.cost ?? 99;
        return ca - cb || (a.name || '').localeCompare(b.name || '');
    });

    const hasCostData = cards.some(c => c.cost != null);
    const hasTypeData = cards.some(c => c.type != null);

    const minCardWidth = viewMode === 'art-name' ? '120px' : '140px';
    const gap = viewMode === 'art-name' ? 1 : 1.25;

    if (grouped && hasCostData) {
        const groups = [];
        let currentCost = undefined;
        let currentGroup = null;
        sortedByCost.forEach(card => {
            const cost = card.cost ?? null;
            if (cost !== currentCost) {
                currentCost = cost;
                currentGroup = { cost, cards: [] };
                groups.push(currentGroup);
            }
            currentGroup.cards.push(card);
        });

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1.5, sm: 2 } }}>
                {groups.map(group => (
                    <Box key={group.cost ?? 'unknown'}>
                        <CostGroupHeader cost={group.cost} cards={group.cards} />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}, 1fr))`,
                                gap
                            }}
                        >
                            {group.cards.map(card => viewMode === 'art-name'
                                ? <ArtNameCard key={card.cardId} card={card} />
                                : <FullCard key={card.cardId} card={card} />
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    const flatSorted = hasTypeData ? sortCardsByType(cards) : sortedByCost;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}, 1fr))`,
                gap,
                p: { xs: 1.5, sm: 2 }
            }}
        >
            {flatSorted.map(card => viewMode === 'art-name'
                ? <ArtNameCard key={card.cardId} card={card} />
                : <FullCard key={card.cardId} card={card} />
            )}
        </Box>
    );
}

function DeckThumbnail({ deck, onClick, sx = {} }) {
    const allCards = Array.isArray(deck?.deckVisual?.cards) ? deck.deckVisual.cards : [];

    // Sort by ink cost (ascending), then by set sort order — same order as the Dreamborn view
    const sorted = [...allCards].sort((a, b) => {
        const ca = a.cost ?? 99;
        const cb = b.cost ?? 99;
        return ca !== cb ? ca - cb : (a.sortNumber || 0) - (b.sortNumber || 0);
    });

    const tiles = sorted.slice(0, 18);
    const hasImages = tiles.some(c => c?.image?.thumbnail || c?.image?.detail);

    if (!hasImages) {
        return (
            <Box onClick={onClick} sx={{
                width: '100%', height: '100%', overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                backgroundColor: 'rgba(76, 29, 149, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                ...sx
            }}>
                <DeckIcon sx={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: 28 }} />
            </Box>
        );
    }

    const cols = Math.ceil(tiles.length / 2);

    return (
        <Box onClick={onClick} sx={{
            width: '100%', height: '100%', overflow: 'hidden',
            cursor: onClick ? 'pointer' : 'default',
            backgroundColor: 'rgba(5, 5, 15, 0.85)',
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '1.5px',
            ...sx
        }}>
            {tiles.map((card, i) => {
                const url = card?.image?.thumbnail || card?.image?.detail;
                return (
                    <Box key={card?.cardId || i} sx={{ overflow: 'hidden', minWidth: 0 }}>
                        {url ? (
                            <img
                                src={url}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', display: 'block' }}
                            />
                        ) : (
                            <Box sx={{ width: '100%', height: '100%', backgroundColor: 'rgba(76, 29, 149, 0.25)' }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

function DeckVisual({
    deck,
    mode = 'card',
    viewMode = 'full-card',
    grouped = true,
    onClick,
    sx = {}
}) {
    const cards = getVisualCards(deck, mode);
    const hasVisualCards = cards.length > 0;
    const cardColumns = getGridColumns(mode, cards.length);
    const cardRows = cardColumns > 0 ? Math.max(1, Math.ceil(cards.length / cardColumns)) : 1;

    if (mode === 'thumbnail') {
        return <DeckThumbnail deck={deck} onClick={onClick} sx={sx} />;
    }

    if (mode === 'full' && hasVisualCards) {
        return (
            <Box sx={{ width: '100%', ...sx }}>
                <DeckVisualFull cards={cards} viewMode={viewMode} grouped={grouped} />
            </Box>
        );
    }

    if (hasVisualCards) {
        return (
            <Box
                onClick={onClick}
                sx={{
                    width: '100%',
                    height: '100%',
                    cursor: onClick ? 'pointer' : 'default',
                    ...sx
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${cardColumns}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${cardRows}, minmax(0, 1fr))`,
                        gap: 0.5,
                        p: 0.5,
                        overflow: 'hidden'
                    }}
                >
                    {cards.map((card) => {
                        const imageUrl = card?.image?.thumbnail || card?.image?.detail;
                        return (
                            <Box
                                key={card.cardId}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 0.8,
                                    overflow: 'hidden',
                                    backgroundColor: 'rgba(76, 29, 149, 0.12)',
                                    border: '1px solid rgba(76, 29, 149, 0.25)',
                                    minHeight: 0,
                                    height: '100%'
                                }}
                            >
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={card.name}
                                        loading="lazy"
                                        decoding="async"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <DeckIcon sx={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: 22 }} />
                                    </Box>
                                )}
                                <QuantityBadge quantity={card.quantity} />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0, right: 0, bottom: 0,
                                        px: 0.6, py: 0.35,
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.2))'
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'white', fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {card.name}{card.subtitle ? ` - ${card.subtitle}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    if (deck?.image_url) {
        return (
            <Box
                onClick={onClick}
                sx={{ width: '100%', height: '100%', cursor: onClick ? 'pointer' : 'default', ...sx }}
            >
                <img
                    src={deck.image_url}
                    alt={deck.name}
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </Box>
        );
    }

    return (
        <Box
            onClick={onClick}
            sx={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(76, 29, 149, 0.12)',
                cursor: onClick ? 'pointer' : 'default',
                ...sx
            }}
        >
            <DeckIcon sx={{ color: 'rgba(124, 58, 237, 0.6)', fontSize: 32 }} />
        </Box>
    );
}

export default DeckVisual;
